import { DownOutlined, StarFilled, UpOutlined } from "@ant-design/icons";
import { Button, Card, Dropdown, Flex, message } from "antd";
import React, { useMemo, useRef, useState } from "react";
import NoteCopyButton from "../../components/CopyButton/CopyButton.component";
import { getSavedInstructionsForNoteType } from "../../components/Customise/customise-note.utils";
import TransferToEHRButton from "../../components/TransferButton/TransferButton.component";
import { NoteType, isCustomisedTemplate } from "../../domain/notes.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { getCustomNote } from "../../service/notes.service";
import {
  setAITemplateModalState,
  setCreateModalState,
  setCustomiseSectionState,
  setEditingState,
  setSelectedNoteForEditing,
  setShowRestrictionModal,
  setStoredTemplateData,
} from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { TNotes } from "../../types/index.type";
import { copyNote, getNoteContentAsText } from "../../utils/datamanipulation.utils";
import EditButton from "./EditButton.component";
import NoteSectionTitle from "./NoteSectionTitle.component";
import styles from "./notes.module.scss";

interface NoteTitleHeaderProps {
  title?: string;
  value?: string;
  hasCustomize?: boolean;
  children?: React.ReactNode;
}

const NoteTitleHeader: React.FC<NoteTitleHeaderProps> = ({ title, value, hasCustomize = true, children }) => {
  const componentName = "notes";
  const dispatch = useAppDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const isMobile = useIsMobileView();
  const { notesDetails } = useAppSelector((state) => state.notes);
  const { isMobilePath } = useAppSelector((state) => state.appState);
  const { customizationList } = useAppSelector((state) => state.client);
  const isCustomTemplateNote = useMemo(
    () => isCustomisedTemplate(notesDetails?.note_type_info?.pk as number, notesDetails?.note_type as number),
    [notesDetails],
  );
  const setEditingMode = (value: boolean, notes: TNotes) => {
    dispatch(setEditingState(value));
    dispatch(setCreateModalState(true));
    dispatch(setSelectedNoteForEditing(notes));
    dispatch(
      setRecordingDetail({
        category_type: notes.category_type,
        language_type: notes.language_type,
        note_type: notes.note_type,
        title: notes.title,
        gender_type: notes.gender_type,
        modality_type: notes.modality_type.toString(),
        location_type: notes.location_type,
        duration_type: notes.duration_type,
        duration_addon_type: notes.duration_addon_type,
        client_id: notes.client,
      }),
    );
  };
  const copyAllNoteContent = () => {
    if (!notesDetails) return;
    copyNote(notesDetails, "", "");
    message.success("Note copied to clipboard");
  };

  const openAITemplateModal = () => {
    if (isMobilePath) {
      dispatch(setShowRestrictionModal(true));
    } else {
      dispatch(setAITemplateModalState(true));
    }
  };

  // For SOAP/DAP notes, show Customize button instead of Improve Quality dropdown
  const isSoapOrDap = notesDetails?.note_type === NoteType.SOAP || notesDetails?.note_type === NoteType.DAP;

  const items = isCustomTemplateNote
    ? [
        {
          key: "1",
          label: "Add One-Time Instructions to fix this note",
          onClick: () => {
            dispatch(setCustomiseSectionState(true));
          },
        },
        {
          key: "2",
          label: "Add Permanent ChatGPT-style Instructions to this template",
          onClick: async () => {
            if (isMobilePath) {
              dispatch(setShowRestrictionModal(true));
              return;
            }
            const loading = message.loading("loading template...");
            if (!notesDetails || !notesDetails.note_type_info || !notesDetails.note_type_info.pk) {
              loading();
              return;
            }
            const template = await getCustomNote(notesDetails.note_type_info.pk as number);
            loading();
            dispatch(
              setStoredTemplateData({
                pk: notesDetails.note_type_info.pk,
                ...template.data,
                document: template.data.document?.replace(/\\n/g, "\n"),
              }),
            );
            openAITemplateModal();
          },
        },
      ]
    : [
        {
          key: "1",
          label: isSoapOrDap ? "Add instructions" : "Add One-Time Instructions to fix this note",
          onClick: () => {
            dispatch(setCustomiseSectionState(true));
          },
        },
        {
          key: "2",
          label: "Create a custom template from this note",
          onClick: async () => {
            if (isMobilePath) {
              dispatch(setShowRestrictionModal(true));
              return;
            }
            if (!notesDetails || !notesDetails.note_type_info) return;
            // Premade template - open Create view with generated note as sample document
            const noteContent = getNoteContentAsText(notesDetails);
            const savedInstructions = getSavedInstructionsForNoteType(customizationList, notesDetails.note_type);
            dispatch(
              setStoredTemplateData({
                name: notesDetails.note_type_info.name,
                document: noteContent,
                appendInstructions: savedInstructions,
                isFromTemplate: true,
              }),
            );
            openAITemplateModal();
          },
        },
      ];

  const ImproveQualityDropdown = ({ className = "" }: { className?: string }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const SPARKLE_ICON_PATH =
      "M2.83333 1V3.66667M1.5 2.33333H4.16667M3.5 10.3333V13M2.16667 11.6667H4.83333M8.16667 1L9.69048 5.57143L13.5 7L9.69048 8.42857L8.16667 13L6.64286 8.42857L2.83333 7L6.64286 5.57143L8.16667 1Z";

    return (
      <div ref={containerRef} className={`${styles["notes__improve-quality-container"]} ${className}`}>
        <Dropdown
          menu={{ items }}
          placement="bottomLeft"
          onOpenChange={setIsDropdownOpen}
          open={isDropdownOpen}
          overlayClassName={styles["notes__improve-quality-dropdown"]}
          getPopupContainer={isMobile ? () => containerRef.current || document.body : undefined}
        >
          <Button
            className={`${styles[`${componentName}__main-section-cust`]}`}
            type="primary"
            style={{ boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)", width: "100%" }}
            icon={
              <svg
                width="15"
                height="14"
                viewBox="0 0 15 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ transition: "transform 0.3s ease" }}
              >
                <path
                  d={SPARKLE_ICON_PATH}
                  stroke="#6366f1"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            Improve my note quality{" "}
            {isDropdownOpen ? (
              <UpOutlined style={{ marginTop: 5, fontSize: 12, transition: "transform 0.3s ease" }} />
            ) : (
              <DownOutlined style={{ marginTop: 5, fontSize: 12, transition: "transform 0.3s ease" }} />
            )}
          </Button>
        </Dropdown>
      </div>
    );
  };

  return (
    <Card className={`${styles[`${componentName}__main-section-header-card`]}`}>
      <Flex gap={10} vertical className={`${styles[`${componentName}__main-section-header-section`]}`}>
        <Flex vertical gap={10} className={`${styles[`${componentName}__main-section-header-section-title`]}`}>
          <Flex align="center" gap={8} wrap="wrap">
            {title && <NoteSectionTitle title={title} />}
            {notesDetails && notesDetails?.note_type < 14 && (
              <span className={styles[`${componentName}__template-badge`]}>
                <StarFilled /> {isMobile ? "Nudge Provided Template" : "This is a Nudge Provided Template"}
              </span>
            )}
          </Flex>
          <Flex
            gap={10}
            className={`${styles[`${componentName}__responsive-button-container`]}`}
            style={{ width: "100%" }}
          >
            <Flex gap={10} className={`${styles[`${componentName}__other-buttons-row`]}`}>
              <EditButton
                onClick={() => {
                  if (notesDetails) setEditingMode(true, notesDetails);
                }}
                className={`${styles[`${componentName}__edit-copy-button`]} ${styles[`${componentName}__responsive-button`]}`}
              />
              {!isMobile && (hasCustomize || isSoapOrDap) && (
                <ImproveQualityDropdown className={`${styles[`${componentName}__responsive-button`]}`} />
              )}
              {isMobile && (
                <NoteCopyButton
                  title={"Copy Note"}
                  onClick={copyAllNoteContent}
                  className={`${styles[`${componentName}__edit-copy-button`]} ${styles[`${componentName}__responsive-button`]}`}
                />
              )}
            </Flex>
            <Flex gap={10} className={`${styles[`${componentName}__other-buttons-row`]}`}>
              {!isMobile && (
                <NoteCopyButton
                  title={"Copy Note"}
                  onClick={copyAllNoteContent}
                  className={`${styles[`${componentName}__edit-copy-button`]} ${styles[`${componentName}__responsive-button`]}`}
                />
              )}
              {isMobile && (hasCustomize || isSoapOrDap) && (
                <ImproveQualityDropdown className={`${styles[`${componentName}__responsive-button`]}`} />
              )}
              {notesDetails && (
                <TransferToEHRButton
                  icon={
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path
                        d="M3.6001 12.6001L0.600098 9.6001M0.600098 9.6001L3.6001 6.6001M0.600098 9.6001H9.6001M9.6001 0.600098L12.6001 3.6001M12.6001 3.6001L9.6001 6.6001M12.6001 3.6001H3.6001"
                        stroke="white"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  className={`${styles[`${componentName}__responsive-button`]} ${styles[`${componentName}__transfer-button`]}`}
                />
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex className={`${styles[`${componentName}__main-section-header-section-content`]}`}>{children}</Flex>
      </Flex>
    </Card>
  );
};

export default NoteTitleHeader;
